import { useMenuLinks } from "Hooks/Layout/useMenuLinks";
import { useCurrentUserAccessRights } from "Hooks/useCurrentUserAccessRights";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { AccessRoleCodes, type AccessRoleCode } from "Models/AccessRoleCodes";
import type { ApplicationLink } from "Types/types";
import { getIsBetaVisible } from "Utils/betaUtils";
import { filterByAccessRightCode } from "Utils/menuUtils";
import { IS_MERCHANT_STORY_AUTHORING_ENABLED } from "Utils/Stories/storyAuthoring";

type UseSettingsMenuReturn = Array<ApplicationLink>;

export const useSettingsMenu = (): UseSettingsMenuReturn => {
  const currentUserRole = useCurrentUserRole();
  const currentUserAccessRights = useCurrentUserAccessRights().map(
    ({ code }) => code,
  );

  const {
    allMerchants,
    bankAccounts,
    licenses,
    profile,
    languageSettings,
    connectedApps,
    thanksToYou,
    contact,
    generalHelp,
    hunters,
    hunterPortal,
    logout,
    legalDocuments,
    employees,
    feeCalculator,
    merchantSettlements,
    merchantPayment,
    paypoints,
    merchants,
    unassignedMerchants,
    stardust,
    prizeDraw,
    raffle,
    campaigns,
    chat,
    coupons,
    rolesPermissions,
    systemControl,
    betaInfo,
    valor,
    people,
    followers,
    peopleSearch,
    stories,
  } = useMenuLinks();

  const userTypeToMenuMap: Record<AccessRoleCode, Array<ApplicationLink>> = {
    [AccessRoleCodes.Admin]: filterByAccessRightCode(
      [
        profile,
        merchants,
        unassignedMerchants,
        hunters,
        campaigns,
        coupons,
        raffle,
        rolesPermissions,
        feeCalculator,
        chat,
        stories,
        languageSettings,
        systemControl,
        betaInfo,
        logout,
      ],
      currentUserAccessRights,
    ),
    [AccessRoleCodes.AdminLite]: filterByAccessRightCode(
      [
        profile,
        merchants,
        unassignedMerchants,
        hunters,
        campaigns,
        coupons,
        raffle,
        rolesPermissions,
        feeCalculator,
        languageSettings,
        systemControl,
        betaInfo,
        logout,
      ],
      currentUserAccessRights,
    ),
    [AccessRoleCodes.Hunter]: filterByAccessRightCode(
      [
        profile,
        hunterPortal,
        bankAccounts,
        merchants,
        valor,
        feeCalculator,
        legalDocuments,
        peopleSearch,
        thanksToYou,
        licenses,
        chat,
        languageSettings,
        connectedApps,
        contact,
        betaInfo,
        logout,
      ],
      currentUserAccessRights,
    ),
    [AccessRoleCodes.HunterPlus]: filterByAccessRightCode(
      [
        profile,
        hunterPortal,
        bankAccounts,
        merchants,
        valor,
        allMerchants,
        unassignedMerchants,
        hunters,
        feeCalculator,
        legalDocuments,
        peopleSearch,
        thanksToYou,
        licenses,
        chat,
        languageSettings,
        connectedApps,
        contact,
        betaInfo,
        logout,
      ],
      currentUserAccessRights,
    ),
    [AccessRoleCodes.Merchant]: filterByAccessRightCode(
      [
        profile,
        bankAccounts,
        merchantPayment,
        employees,
        paypoints,
        merchantSettlements,
        legalDocuments,
        followers,
        thanksToYou,
        chat,
        // Brand-only launch: merchants get the Stories page only once their authoring is switched on.
        ...(IS_MERCHANT_STORY_AUTHORING_ENABLED ? [stories] : []),
        languageSettings,
        connectedApps,
        contact,
        generalHelp,
        betaInfo,
        logout,
      ],
      currentUserAccessRights,
    ),
    [AccessRoleCodes.Customer]: filterByAccessRightCode(
      [
        profile,
        bankAccounts,
        stardust,
        prizeDraw,
        legalDocuments,
        people,
        thanksToYou,
        chat,
        languageSettings,
        connectedApps,
        contact,
        generalHelp,
        betaInfo,
        logout,
      ],
      currentUserAccessRights,
    ),
    [AccessRoleCodes.Aml]: filterByAccessRightCode(
      [contact, languageSettings, betaInfo, logout],
      currentUserAccessRights,
    ),
    [AccessRoleCodes.Employee]: [
      profile,
      generalHelp,
      languageSettings,
      connectedApps,
      betaInfo,
      logout,
    ],
    [AccessRoleCodes.CustomerOnboarding]: filterByAccessRightCode(
      [
        chat,
        unassignedMerchants,
        coupons,
        contact,
        languageSettings,
        betaInfo,
        logout,
      ],
      currentUserAccessRights,
    ),
    Disponent: [],
    Child: [],
  };

  const menu = currentUserRole ? userTypeToMenuMap[currentUserRole] : [];

  return getIsBetaVisible() ? menu : menu.filter((link) => link !== betaInfo);
};
